import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import {
  createPronunciation,
  deletePronunciation,
  reorderPronunciations,
  updatePronunciation,
} from '@polyai/stream/store/reducers/pronunciations/actions';
import type {
  Pronunciation,
  Pronunciations,
} from '@polyai/stream/store/reducers/pronunciations/types';
import { assignDefinedFields } from '@polyai/stream/store/utils/assign-fields';
import { assignMetadata } from '@polyai/stream/store/utils/assign-metadata';

const pronunciationAdapter = createEntityAdapter<Pronunciation>();
const pronunciationSelectors = pronunciationAdapter.getSelectors();

const initialState = {
  pronunciations: pronunciationAdapter.getInitialState(),
} satisfies Pronunciations;

const slice = createSlice({
  name: 'pronunciations',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(createPronunciation, (state, action) => {
      const pronunciation: Pronunciation = {
        ...action.payload,
        languageCode: action.payload.languageCode ?? '',
        description: action.payload.description ?? '',
        position: action.payload.position ?? 0,
        name: action.payload.name ?? '',
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
      };
      assignMetadata(action, pronunciation);
      pronunciationAdapter.addOne(state.pronunciations, pronunciation);
    });

    builder.addCase(updatePronunciation, (state, action) => {
      const pronunciation = pronunciationSelectors.selectById(
        state.pronunciations,
        action.payload.id!,
      );
      if (pronunciation) {
        const updatedPronunciation = { ...pronunciation };
        assignDefinedFields(updatedPronunciation, action.payload);
        assignMetadata(action, updatedPronunciation);
        pronunciationAdapter.updateOne(state.pronunciations, {
          id: action.payload.id!,
          changes: updatedPronunciation,
        });
      }
    });

    builder.addCase(deletePronunciation, (state, action) => {
      pronunciationAdapter.removeOne(state.pronunciations, action.payload.id);
    });

    builder.addCase(reorderPronunciations, (state, action) => {
      action.payload.newPositions.forEach(positionUpdate => {
        const pronunciation = pronunciationSelectors.selectById(
          state.pronunciations,
          positionUpdate.id,
        );
        if (pronunciation) {
          const updatedPronunciation = {
            ...pronunciation,
            position: positionUpdate.position,
          };
          assignMetadata(action, updatedPronunciation);
          pronunciationAdapter.updateOne(state.pronunciations, {
            id: positionUpdate.id,
            changes: updatedPronunciation,
          });
        }
      });
    });
  },
});

export type { Pronunciation };

export { pronunciationAdapter };

export { pronunciationSelectors };

export default slice.reducer;
